/**
 * Output Style Profiles
 * Category-based formatting for agent outputs.
 * Different agents should think and speak differently.
 */

// Style profiles by agent category
export const OUTPUT_STYLE_PROFILES = {
  // 🧠 Analyst Agents - Structured, data-led
  analyst: {
    id: 'analyst',
    name: 'Analyst',
    icon: 'BarChart3',
    tone: 'analytical',
    characteristics: [
      'Structured and methodical',
      'Data-led with quantified impact',
      'Clear headings and bullet points',
      'Emphasis on insights and trade-offs',
      'Evidence-based conclusions'
    ],
    formatting: {
      useBulletPoints: true,
      useNumberedLists: true,
      useTables: true,
      useQuantification: true,
      emphasisStyle: 'data-driven',
      sectionOrder: ['summary', 'key_findings', 'analysis', 'implications', 'recommendations']
    },
    exportTemplate: 'analyst_report',
    colorAccent: '#6366F1' // Indigo
  },

  // ⚙️ Operations Agents - Action-oriented
  operations: {
    id: 'operations',
    name: 'Operations',
    icon: 'Settings',
    tone: 'actionable',
    characteristics: [
      'Action-oriented and practical',
      'Checklists, steps, and timelines',
      'Clear ownership and next steps',
      'Minimal theory, maximum clarity',
      'Process-focused'
    ],
    formatting: {
      useBulletPoints: true,
      useChecklists: true,
      useTimelines: true,
      useQuantification: false,
      emphasisStyle: 'action-focused',
      sectionOrder: ['summary', 'immediate_actions', 'process_steps', 'timeline', 'ownership']
    },
    exportTemplate: 'operations_report',
    colorAccent: '#14F195' // Emerald
  },

  // 💰 Finance Agents - Conservative, risk-aware
  finance: {
    id: 'finance',
    name: 'Finance',
    icon: 'DollarSign',
    tone: 'conservative',
    characteristics: [
      'Conservative and precise',
      'Assumptions clearly stated',
      'Risk and compliance emphasis',
      'Tables and numerical data',
      'Cautious recommendations'
    ],
    formatting: {
      useBulletPoints: true,
      useTables: true,
      useQuantification: true,
      useDisclaimers: true,
      emphasisStyle: 'risk-aware',
      sectionOrder: ['summary', 'key_metrics', 'assumptions', 'risks', 'compliance', 'recommendations']
    },
    exportTemplate: 'finance_report',
    colorAccent: '#F59E0B' // Amber
  },

  // 🧑‍💼 Strategy / Executive Agents - High-level synthesis
  strategy: {
    id: 'strategy',
    name: 'Strategy',
    icon: 'Briefcase',
    tone: 'executive',
    characteristics: [
      'High-level synthesis',
      'Clear strategic options',
      'Pros/cons analysis',
      'Decision framing',
      'Executive-ready language'
    ],
    formatting: {
      useBulletPoints: true,
      useProsCons: true,
      useQuantification: false,
      useDecisionFramework: true,
      emphasisStyle: 'strategic',
      sectionOrder: ['executive_summary', 'strategic_context', 'options', 'recommendation', 'next_steps']
    },
    exportTemplate: 'strategy_report',
    colorAccent: '#8B5CF6' // Purple
  },

  // 👥 HR / People Agents
  hr: {
    id: 'hr',
    name: 'HR & People',
    icon: 'Users',
    tone: 'supportive',
    characteristics: [
      'People-focused',
      'Empathetic tone',
      'Clear communication',
      'Policy-aware',
      'Developmental focus'
    ],
    formatting: {
      useBulletPoints: true,
      useQuantification: false,
      emphasisStyle: 'people-focused',
      sectionOrder: ['summary', 'key_points', 'impact', 'recommendations', 'next_steps']
    },
    exportTemplate: 'hr_report',
    colorAccent: '#EC4899' // Pink
  },

  // 📈 Sales Agents
  sales: {
    id: 'sales',
    name: 'Sales',
    icon: 'TrendingUp',
    tone: 'results-driven',
    characteristics: [
      'Results-driven',
      'Opportunity-focused',
      'Clear value propositions',
      'Action-oriented',
      'Customer-centric'
    ],
    formatting: {
      useBulletPoints: true,
      useQuantification: true,
      emphasisStyle: 'opportunity-focused',
      sectionOrder: ['summary', 'opportunity', 'value_proposition', 'next_steps', 'timeline']
    },
    exportTemplate: 'sales_report',
    colorAccent: '#10B981' // Green
  },

  // 📢 Marketing Agents
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    icon: 'Megaphone',
    tone: 'creative',
    characteristics: [
      'Creative and engaging',
      'Brand-aware',
      'Audience-focused',
      'Metric-conscious',
      'Trend-aware'
    ],
    formatting: {
      useBulletPoints: true,
      useQuantification: true,
      emphasisStyle: 'creative',
      sectionOrder: ['summary', 'insights', 'recommendations', 'metrics', 'next_steps']
    },
    exportTemplate: 'marketing_report',
    colorAccent: '#F472B6' // Light Pink
  },

  // 📋 General / Default
  general: {
    id: 'general',
    name: 'General',
    icon: 'FileText',
    tone: 'professional',
    characteristics: [
      'Clear and professional',
      'Well-structured',
      'Balanced perspective',
      'Actionable insights'
    ],
    formatting: {
      useBulletPoints: true,
      useQuantification: false,
      emphasisStyle: 'balanced',
      sectionOrder: ['summary', 'analysis', 'findings', 'recommendations']
    },
    exportTemplate: 'general_report',
    colorAccent: '#6B7280' // Gray
  }
};

/**
 * Get output style profile by category
 */
export const getOutputStyleProfile = (category) => {
  const normalizedCategory = (category || 'general').toLowerCase();
  return OUTPUT_STYLE_PROFILES[normalizedCategory] || OUTPUT_STYLE_PROFILES.general;
};

/**
 * Detect category from agent name or metadata
 */
export const detectAgentCategory = (agentName, metadata = {}) => {
  // Check explicit category in metadata
  if (metadata.category) {
    const cat = metadata.category.toLowerCase();
    if (OUTPUT_STYLE_PROFILES[cat]) return cat;
  }
  
  const name = (agentName || '').toLowerCase();
  
  // Pattern matching
  if (name.includes('analyst') || name.includes('analysis') || name.includes('data')) return 'analyst';
  if (name.includes('ops') || name.includes('operation') || name.includes('process')) return 'operations';
  if (name.includes('finance') || name.includes('financial') || name.includes('accounting') || name.includes('budget')) return 'finance';
  if (name.includes('strategy') || name.includes('exec') || name.includes('leadership') || name.includes('ceo') || name.includes('board')) return 'strategy';
  if (name.includes('hr') || name.includes('people') || name.includes('talent') || name.includes('recruit')) return 'hr';
  if (name.includes('sales') || name.includes('revenue') || name.includes('deal')) return 'sales';
  if (name.includes('marketing') || name.includes('brand') || name.includes('campaign')) return 'marketing';
  
  return 'general';
};

/**
 * Format output content based on category style profile
 * This applies styling rules to the rendered output
 */
export const formatOutputByCategory = (content, category) => {
  const profile = getOutputStyleProfile(category);
  
  // Return formatted content metadata for rendering
  return {
    profile,
    accentColor: profile.colorAccent,
    tone: profile.tone,
    formatting: profile.formatting,
    sectionOrder: profile.formatting.sectionOrder
  };
};

/**
 * Get section display info based on style profile
 */
export const getSectionDisplayInfo = (sectionKey, category) => {
  const profile = getOutputStyleProfile(category);
  const normalizedKey = (sectionKey || '').toLowerCase().replace(/[_\s]+/g, '_');
  
  // Section-specific styling
  const sectionStyles = {
    summary: { icon: 'Target', priority: 1, highlight: true },
    executive_summary: { icon: 'Target', priority: 1, highlight: true },
    key_findings: { icon: 'Search', priority: 2, highlight: false },
    analysis: { icon: 'BarChart3', priority: 3, highlight: false },
    recommendations: { icon: 'Lightbulb', priority: 4, highlight: true },
    next_steps: { icon: 'ArrowRight', priority: 5, highlight: false },
    risks: { icon: 'AlertTriangle', priority: 3, highlight: profile.id === 'finance' },
    compliance: { icon: 'Shield', priority: 4, highlight: profile.id === 'finance' },
    timeline: { icon: 'Calendar', priority: 4, highlight: profile.id === 'operations' },
    metrics: { icon: 'TrendingUp', priority: 3, highlight: false },
    assumptions: { icon: 'HelpCircle', priority: 3, highlight: profile.id === 'finance' }
  };
  
  return sectionStyles[normalizedKey] || { icon: 'FileText', priority: 10, highlight: false };
};

/**
 * Apply tone-specific text transformations
 */
export const applyToneTransformation = (text, tone) => {
  // This is a placeholder for future AI-powered tone adjustments
  // For now, return text as-is but with metadata
  return {
    text,
    tone,
    transformations: []
  };
};

export default {
  OUTPUT_STYLE_PROFILES,
  getOutputStyleProfile,
  detectAgentCategory,
  formatOutputByCategory,
  getSectionDisplayInfo,
  applyToneTransformation
};
